/**
 * 个人知识库上传前的**前端预检**(2026-09-07,一期)。
 *
 * 真正的闸在后端(单文件 ≤20MB、每人 ≤20 份,超限返回人话 message,前端直接 toast)——
 * 这里的判据只是省一次网络往返:选完文件当场告诉用户"这份太大"/"传不下了",而不是等
 * multipart 传完了才在 catch 里读 ApiError.message。两边数值必须与后端同口径,数字改了
 * 两边一起改,别只改这一处让用户"明明没超却传不上"或反过来"传上了才被后端拒"。
 */

/** 单文件大小上限(MB)。 */
export const PERSONAL_DOC_MAX_MB = 20;

/** 每人个人文档份数上限。 */
export const PERSONAL_DOC_MAX_COUNT = 20;

/** input accept 属性;与后端允许的文档类型对齐(pdf/word/纯文本/markdown)。 */
export const PERSONAL_DOC_ACCEPT = ".pdf,.docx,.doc,.txt,.md";

/** 单文件超大小 → 一句人话;没超 → null。 */
export function personalDocSizeError(f: File): string | null {
  if (f.size <= PERSONAL_DOC_MAX_MB * 1024 * 1024) return null;
  return `《${f.name}》超过 ${PERSONAL_DOC_MAX_MB}MB,请压缩或拆分后再传`;
}

export type PersonalDocUploadPartition = {
  /** 通过预检、可以真正发起上传的文件。 */
  accepted: File[];
  /** 单文件超过 20MB 被挡下的。 */
  oversized: File[];
  /** 大小合规,但加上已有份数会超过 20 份总量上限,被挡下的。 */
  overLimit: File[];
};

/**
 * 从一批待上传文件里筛出「大小合规 + 不超过总量上限」的子集。
 *
 * `currentCount` 是**当前已有的个人文档数**(promote 掉的不算——那份已经从个人列表里消失了,
 * 不该占个人库的份数)。超大小和超总量是两种独立的失败原因,分开返回而不是合并成一句
 * "部分文件未上传",调用方才能各自给出对应的提示文案。
 */
export function partitionPersonalDocUploads(
  files: File[],
  currentCount: number
): PersonalDocUploadPartition {
  const oversized: File[] = [];
  const sizeOk = files.filter((f) => {
    if (personalDocSizeError(f)) {
      oversized.push(f);
      return false;
    }
    return true;
  });
  const remainingSlots = Math.max(0, PERSONAL_DOC_MAX_COUNT - currentCount);
  const accepted = sizeOk.slice(0, remainingSlots);
  const overLimit = sizeOk.slice(remainingSlots);
  return { accepted, oversized, overLimit };
}

/**
 * 份数计数预警文案。未接近上限(<18/20)返回 null——平时不必打扰用户;
 * 达到/超过 18 份开始提示,20 份时说清楚"传不了了"而不是继续说"快满了"。
 */
export function personalDocCountWarning(currentCount: number): string | null {
  if (currentCount >= PERSONAL_DOC_MAX_COUNT) {
    return `已达 ${PERSONAL_DOC_MAX_COUNT} 份上限,请先删除或共享部分文档到租户库后再传`;
  }
  if (currentCount >= PERSONAL_DOC_MAX_COUNT - 2) {
    return `已有 ${currentCount}/${PERSONAL_DOC_MAX_COUNT} 份,快到上限了`;
  }
  return null;
}
